
import { useState } from 'react';
import { useAICostData } from './hooks/useAICostData';
import { AICostOverview } from './AICostOverview';
import { AICostChart } from './AICostChart';
import { AICostTransactions } from './AICostTransactions';
import { TimeRangeFilter } from './TimeRangeFilter';
import { Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';

export function AICostMonitoring() {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  
  const { 
    chartData,
    costDetails,
    totalCost,
    totalTokens,
    totalUsers,
    isLoading, 
    error,
    refetch
  } = useAICostData(timeRange);

  const handleRetry = () => {
    refetch();
  };

  // Handle error state
  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error loading AI cost data</AlertTitle>
          <AlertDescription>
            {typeof error === 'string' ? error : error.message || 'An unknown error occurred while fetching data'}
          </AlertDescription>
        </Alert>
        
        <div className="flex justify-center">
          <Button onClick={handleRetry} variant="outline" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </div>
        
        <div className="p-6 border rounded-md bg-muted/20">
          <h3 className="text-lg font-medium mb-2">Troubleshooting</h3>
          <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
            <li>Check if you have admin privileges in the system</li>
            <li>Verify that the AI cost monitoring tables exist in your database</li>
            <li>Ensure the fetch_ai_cost_data database function is properly set up</li>
            <li>Check browser console for more detailed error information</li>
          </ul>
        </div>
      </div>
    );
  }

  // Handle loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Handle empty data state
  if (chartData.length === 0) {
    return (
      <div className="space-y-6">
        <TimeRangeFilter value={timeRange} onChange={setTimeRange} />
        
        <Alert className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No data available</AlertTitle>
          <AlertDescription>
            No AI cost data was found for the selected time period.
          </AlertDescription>
        </Alert>
        
        <AICostOverview 
          timeRange={timeRange}
          totalCost={0}
          totalTokens={0}
          totalUsers={0}
        />
      </div>
    );
  }

  // Success state with data
  return (
    <div className="space-y-6">
      <TimeRangeFilter value={timeRange} onChange={setTimeRange} />
      
      <AICostOverview 
        timeRange={timeRange}
        totalCost={totalCost}
        totalTokens={totalTokens}
        totalUsers={totalUsers}
      />
      
      <div className="grid grid-cols-1 gap-6">
        <AICostChart chartData={chartData} />
        <AICostTransactions transactions={costDetails} />
      </div>
    </div>
  );
}
